import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import {
  collection, query, where, onSnapshot,
  addDoc, getDocs, limit, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import StudentCurriculum from './StudentCurriculum';
import StudentTaskView from './StudentTaskView';

const STATUS = {
  pending: {
    color: '#FF8F00',
    icon: '⏳',
    label: 'Request Pending',
    message: (school) =>
      `Your request to join ${school} is waiting for teacher approval.`,
  },
  approved: {
    color: '#2E7D32',
    icon: '✅',
    label: "You're In!",
    message: (school) =>
      `Your teacher has approved your request to join ${school}.`,
  },
  denied: {
    color: '#C62828',
    icon: '❌',
    label: 'Request Not Approved',
    message: (school) =>
      `Your request to join ${school} was not approved. Search for another school below.`,
  },
};

// ── School Search ─────────────────────────────────────────────────────────────

function SchoolSearch() {
  const { currentUser, userProfile } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;
    setSearching(true);
    setSearchResults([]);
    setHasSearched(true);
    try {
      const q = query(
        collection(db, 'schools'),
        where('nameLower', '>=', term.toLowerCase()),
        where('nameLower', '<=', term.toLowerCase() + '\uf8ff'),
        limit(10)
      );
      const snap = await getDocs(q);
      setSearchResults(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error('Search error:', err); }
    setSearching(false);
  };

  const handleSendRequest = async (school) => {
    setSendingTo(school.teacherId);
    try {
      await addDoc(collection(db, 'joinRequests'), {
        studentId: currentUser.uid,
        studentName: userProfile.name,
        studentEmail: currentUser.email,
        teacherId: school.teacherId,
        teacherName: school.teacherName,
        schoolName: school.name,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
    } catch (err) { console.error('Request error:', err); }
    setSendingTo(null);
  };

  return (
    <div className="section">
      <div className="section-header-row" style={{ marginBottom: 14 }}>
        <h3 className="section-title" style={{ marginBottom: 0 }}>🔍 Find Your School</h3>
      </div>
      <form onSubmit={handleSearch} className="search-form">
        <input
          type="text"
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Start typing your school name…"
        />
        <button type="submit" className="btn-primary" style={{ width: 'auto', marginTop: 0, padding: '11px 20px', flexShrink: 0 }} disabled={searching}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {hasSearched && !searching && searchResults.length === 0 && (
        <div className="empty-state" style={{ marginTop: 16 }}>
          <span>🔎</span>
          <p>No schools found for "{searchTerm}".<br />Ask your teacher to sign up on LittleNest first.</p>
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="search-results" style={{ marginTop: 12 }}>
          {searchResults.map((school) => (
            <div key={school.id} className="school-card">
              <div className="school-info">
                <strong>{school.name}</strong>
                <span className="school-meta">{school.city} · {school.board}</span>
                <span className="school-teacher">Teacher: {school.teacherName}</span>
              </div>
              <button
                className="btn-join"
                onClick={() => handleSendRequest(school)}
                disabled={!!sendingTo}
              >
                {sendingTo === school.teacherId ? 'Sending…' : 'Send Request'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Student Dashboard ─────────────────────────────────────────────────────────

export default function StudentDashboard() {
  const { currentUser, userProfile } = useAuth();
  const [joinRequest, setJoinRequest] = useState(undefined);
  const [loadingData, setLoadingData] = useState(true);
  
  // Dashboard stats
  const [tasks, setTasks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  
  // view: 'dashboard' | 'curriculum' | 'task'
  const [view, setView] = useState('dashboard');
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'joinRequests'),
      where('studentId', '==', currentUser.uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const latest = snap.docs[snap.docs.length - 1].data();
        setJoinRequest({ id: snap.docs[snap.docs.length - 1].id, ...latest });
      } else {
        setJoinRequest(null);
      }
      setLoadingData(false);
    });
    return unsub;
  }, [currentUser]);

  // Listen for tasks + submissions to show stats
  useEffect(() => {
    const teacherId = joinRequest?.teacherId;
    const isApproved = joinRequest?.status === 'approved';
    if (!currentUser?.uid || !teacherId || !isApproved) return;
    
    const tasksUnsub = onSnapshot(
      query(
        collection(db, 'tasks'),
        where('assignedStudentIds', 'array-contains', currentUser.uid)
      ),
      (snap) => setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const subsUnsub = onSnapshot(
      query(
        collection(db, 'taskSubmissions'),
        where('studentId', '==', currentUser.uid)
      ),
      (snap) => setSubmissions(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    return () => { tasksUnsub(); subsUnsub(); };
  }, [currentUser?.uid, joinRequest?.id, joinRequest?.status, joinRequest?.teacherId]);

  const handleSignOut = () => signOut(auth);

  const firstName = userProfile?.name?.split(' ')[0] || 'Student';

  // Calculate pending
  const submittedIds = submissions.map((s) => s.taskId);
  const pendingTasks = tasks.filter((t) => !submittedIds.includes(t.id));

  // ── Task View ──
  if (view === 'task' && selectedTask) {
    return (
      <StudentTaskView
        task={selectedTask}
        allSubmissions={submissions}
        onBack={() => {
          setView('dashboard');
          setSelectedTask(null);
        }}
      />
    );
  }

  // ── Curriculum view ──
  if (view === 'curriculum' && joinRequest?.status === 'approved') {
    return (
      <StudentCurriculum
        teacherId={joinRequest.teacherId}
        teacherName={joinRequest.teacherName}
        schoolName={joinRequest.schoolName}
        allTasks={tasks}
        allSubmissions={submissions}
        onBack={() => setView('dashboard')}
        onTaskSelect={(task) => {
          setSelectedTask(task);
          setView('task');
        }}
      />
    );
  }

  return (
    <div className="dashboard-wrapper">
      <header className="dashboard-header">
        <div className="header-left">
          <div className="student-avatar" style={{ width: 44, height: 44, fontSize: '18px', flexShrink: 0 }}>
            {firstName.charAt(0)}
          </div>
          <div className="header-info">
            <h2 className="header-title">Hello, {firstName}!</h2>
            <p className="header-subtitle">Student Learning Hub</p>
          </div>
        </div>
        <button className="btn-outline btn-outline--sm" onClick={handleSignOut}>Sign Out</button>
      </header>

      {loadingData && (
        <div className="animate-in">
          <div className="stats-row" style={{ marginTop: 24 }}>
            <div className="stat-card skeleton" style={{ height: 100 }}></div>
            <div className="stat-card skeleton" style={{ height: 100 }}></div>
          </div>
          <div className="section" style={{ marginTop: 32 }}>
            <div className="skeleton" style={{ height: 24, width: 200, marginBottom: 16 }}></div>
            <div className="task-quick-list">
              {[1, 2, 3].map((i) => (
                <div key={i} className="task-row skeleton" style={{ height: 74, border: 'none' }}></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* No request yet */}
      {!loadingData && joinRequest === null && (
        <div className="animate-in">
          <div className="section">
            <div className="onboarding-card">
              <div className="onboarding-icon">🏫</div>
              <h3>Find your teacher</h3>
              <p>Search for your school and send a join request to your teacher.</p>
            </div>
          </div>
          <SchoolSearch />
        </div>
      )}

      {/* Has request */}
      {!loadingData && joinRequest && (
        <div className="animate-in">
          <div className="section">
            <div
              className="status-card"
              style={{ borderLeftColor: STATUS[joinRequest.status]?.color }}
            >
              <span className="status-icon">{STATUS[joinRequest.status]?.icon}</span>
              <div>
                <strong>{STATUS[joinRequest.status]?.label}</strong>
                <p>{STATUS[joinRequest.status]?.message(joinRequest.schoolName)}</p>
              </div>
            </div>

            {joinRequest.status === 'approved' && (
              <>
                <div className="stats-row" style={{ marginTop: 24 }}>
                  <div className="stat-card">
                    <span className="stat-num">{tasks.length}</span>
                    <span className="stat-label">Assigned Tasks</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-num">{pendingTasks.length}</span>
                    <span className="stat-label">Pending</span>
                  </div>
                </div>

                <div className="section" style={{ marginTop: 32 }}>
                  <div className="section-header-row">
                    <h3 className="section-title">🕒 Pending Tasks</h3>
                    <button className="btn-link" onClick={() => setView('curriculum')}>View All Curriculum ›</button>
                  </div>
                  
                  <div className="task-quick-list">
                    {pendingTasks.length === 0 ? (
                      <div className="empty-state-sm">
                        <span>✨</span>
                        <p>All caught up! No pending tasks.</p>
                      </div>
                    ) : (
                      pendingTasks.slice(0, 3).map((t, idx) => (
                        <div 
                          key={t.id} 
                          className="task-row animate-in"
                          style={{ animationDelay: `${idx * 0.1}s` }}
                          onClick={() => {
                            setSelectedTask(t);
                            setView('task');
                          }}
                        >
                          <div className="task-row-left">
                            <span className="task-icon">📋</span>
                            <div className="task-info">
                              <span className="task-title">{t.title}</span>
                              <span className="task-meta">{t.subjectName} · {t.chapterName}</span>
                            </div>
                          </div>
                          <span className="task-arrow">›</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="approved-welcome" style={{ marginTop: 40 }}>
                  <div className="welcome-icon">🎉</div>
                  <h3>Welcome to {joinRequest.schoolName}!</h3>
                  <p>
                    Explore your full curriculum and textbook resources.
                  </p>
                  <button
                    className="btn-primary"
                    style={{ width: 'auto', marginTop: 8 }}
                    onClick={() => setView('curriculum')}
                  >
                    📚 Browse Curriculum
                  </button>
                </div>
              </>
            )}
          </div>

          {joinRequest.status === 'denied' && <SchoolSearch />}
        </div>
      )}
    </div>
  );
}
